import { useEffect } from 'react';
import useApi from './useApi';
import useApiEndpoints from '../context/useApiEndpoints';
import { createReportMetadata, createReportMetadataList } from '../factory/Verification/ReportMetadataFactory';
import { ReportMetadata } from '../types/ViewModel/Verification/ReportMetadata';
import { DnvResponseBM } from '../types/BusinessModel/Verification/DnvResponseBM';
import { DnvResponse } from '../types/ViewModel/Verification/DnvResponse';
import { DNVReportStatus } from '../enums/dnvReportStatusEnum';
import { buildQueryParams } from './endpoints/queryParamsHelper';
import { ReportComment } from '../types/ViewModel/Verification/ReportComment';
import apiEndpointUtils from 'src/utils/apiEndpointUtils';
import { useSWService } from './sw/useSWService';

export default () => {
    const { apiEndpoints } = useApiEndpoints();
    const { processApiEndpointError } = apiEndpointUtils();
    const { state, postApiData } = useApi();
    const { removeRequestsContaining } = useSWService();

    const invalidateVerificationCache = async () => {
        // Invalidate cache for both DNV verification endpoints
        await removeRequestsContaining([apiEndpoints['getDNVReportsMetadata'].url, apiEndpoints['getDNVReportsMetadataForShip'].url]);
    };

    const sendReport = async (reportMetadata: ReportMetadata) => {
        const result = await postApiData({
            endpoint: apiEndpoints['sendReport'].url,
            data: reportMetadata,
        });

        if (result) {
            // Invalidate verification cache after successful send
            await invalidateVerificationCache();

            const dnvResponseBM = result as DnvResponseBM;
            const reportMetadata = dnvResponseBM.metadata ? createReportMetadata(dnvResponseBM.metadata) : null;
            const dnvResponse: DnvResponse = {
                metadata: reportMetadata,
                errors: dnvResponseBM.errors,
                warnings: dnvResponseBM.warnings,
            };
            return dnvResponse;
        }
    };

    const sendReports = async (reportIds: string) => {
        const result = await postApiData({
            endpoint: apiEndpoints['sendReports'].url,
            queryParam: buildQueryParams({ reportIds: reportIds }),
        });

        if (result) {
            // Invalidate verification cache after successful send
            await invalidateVerificationCache();

            const reportMetadataListBM = result as any;
            const reportMetadataList = createReportMetadataList(reportMetadataListBM);
            return reportMetadataList;
        }
    };

    const updateReportStatus = async (reportId: number, reportStatus: DNVReportStatus) => {
        const result = await postApiData({
            endpoint: apiEndpoints['updateReportStatus'].url,
            queryParam: buildQueryParams({ reportId: reportId, reportStatus: reportStatus }),
        });

        return result;
    };

    const acceptReport = async (reportId: number, accept: boolean, comment: ReportComment) => {
        const result = await postApiData({
            endpoint: apiEndpoints['acceptReport'].url,
            queryParam: buildQueryParams({ reportId, accept }),
            data: comment,
        });

        return result;
    };

    const acceptInternalIssue = async (issueId: number, accept: boolean) => {
        const result = await postApiData({
            endpoint: apiEndpoints['acceptInternalIssue'].url,
            queryParam: buildQueryParams({ issueId, accept }),
        });

        return result;
    };

    useEffect(() => {
        processApiEndpointError(state);
    }, [state]);

    return {
        sendReport,
        sendReports,
        updateReportStatus,
        acceptReport,
        acceptInternalIssue,
    };
};
